import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';
import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';
import { TypedMemo } from '../../common/types';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import { assetShareSubtitleFact } from './assetShareFacts';
import { assetShareMessages } from './assetShareMessages';
import { AssetSharePreviewChip } from './AssetSharePreviewChip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** "LaziiBee · Visualizer · 3:54", "LaziiBee · Cover · 3000 × 3000". */
export function shareSubtitle(share: PublicAssetShare): string {
  return [share.artist.name, share.assetLabel, assetShareSubtitleFact(share)]
    .filter((part) => part.length > 0)
    .join(' · ');
}

/**
 * The info of the theater page (design asset-share c1-c5): the cover of the song, the song title,
 * the artist, the kind of the file and its length or size. On a phone the Share icon sits at the end
 * of the row.
 */
const AssetShareIdentityComponent: React.FC<{ share: PublicAssetShare; onShare: () => void }> = ({
  share,
  onShare,
}) => {
  const intl = useIntl();
  const subtitle = useMemo(() => shareSubtitle(share), [share]);
  const artwork = share.song.artworkUrl ?? share.artist.avatarUrl;
  return (
    <div className={styles.identity}>
      {artwork !== null && share.mediaKind === 'video' && (
        <img className={styles.thumb} src={getFullAPIFileURL(artwork)} alt="" />
      )}
      <div className={styles.identityText}>
        <h1 className={styles.song}>{share.song.title}</h1>
        <p className={styles.subtitle}>
          <span>{subtitle}</span>
          {share.preview && <AssetSharePreviewChip className="ms-2" />}
        </p>
      </div>
      <button
        type="button"
        className={clsx(styles.iconButton, 'd-lg-none')}
        onClick={onShare}
        aria-label={intl.formatMessage(assetShareMessages.share)}
        data-testid="asset-share-share"
      >
        <FontAwesomeIcon icon={faArrowUpFromBracket} />
      </button>
    </div>
  );
};

export const AssetShareIdentity = TypedMemo(AssetShareIdentityComponent);
